import React from 'react';
import { Box, Container } from '@chakra-ui/react';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { useTranslation } from 'react-i18next';
import PremiumAssetsLoader from './PremiumAssetsLoader';
import { assetUrl } from '../../utils/url';
import { useAuth } from '../../contexts/AuthContext';
import { useClubTheme } from '../../hooks/useClubTheme';
import SpartaNavbar from '../../components/elements/SpartaNavbar';
import SponsorsSection from '../../components/common/SponsorsSection';
import Footer from '../../components/layout/Footer';

const LegacyPremiumLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: s } = usePublicSettings();
  const { t } = useTranslation();
  const theme = useClubTheme();
  const clubLogo = s?.club_logo_url || '/dist/img/logo-club-empty.svg';
  const clubName = s?.club_name || 'Fotbal Club';
  const galleryUrl = s?.gallery_url || s?.zonerama_url || undefined;
  const { isAuthenticated, user, logout } = useAuth();
  const role = String(user?.role || '').toLowerCase();
  const accountHref = role === 'admin' || role === 'editor' ? '/admin' : '/semiadmin';

  React.useEffect(() => {
    try {
      const root = document.documentElement as HTMLElement;
      root.style.setProperty('--main', theme.primaryColor);
      root.style.setProperty('--second', theme.secondaryColor);
      root.style.setProperty('--accent', theme.accentColor);
      root.style.setProperty('--background', theme.backgroundColor);
      document.body.style.backgroundColor = theme.backgroundColor;
    } catch {}
  }, [theme.primaryColor, theme.secondaryColor, theme.accentColor, theme.backgroundColor]);

  React.useEffect(() => {
    const t = setTimeout(() => {
      try {
        const w: any = window as any;
        if (typeof w.initStyles === 'function') { w.initStyles(); }
        if (typeof w.setResizeStyles === 'function') { w.setResizeStyles(); }
        if (typeof w.checkNavbar === 'function') { w.checkNavbar(); }
        if (typeof w.initMasonry === 'function') { w.initMasonry(); }
        if (typeof w.initParallax === 'function') { w.initParallax(); }
        if ((w.jQuery || w.$) && typeof (w.jQuery || w.$)(window).trigger === 'function') {
          (w.jQuery || w.$)(window).trigger('resize');
        } else {
          window.dispatchEvent(new Event('resize'));
        }
      } catch {}
    }, 50);
    return () => clearTimeout(t);
  }, []);

  React.useEffect(() => {
    const w: any = window as any;
    let raf = 0;
    const relayout = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try { if (typeof w.initMasonry === 'function') w.initMasonry(); } catch {}
      });
    };
    // Reflow on new images loading inside masonry containers
    const imgs: NodeListOf<HTMLImageElement> = document.querySelectorAll('.masonry img');
    imgs.forEach((img) => { if (!img.complete) img.addEventListener('load', relayout, { once: true } as any); });
    // Observe DOM mutations inside masonry containers
    const containers = document.querySelectorAll('.masonry');
    const observers: MutationObserver[] = [];
    containers.forEach((c) => {
      const mo = new MutationObserver(() => relayout());
      mo.observe(c, { childList: true, subtree: true });
      observers.push(mo);
    });
    // Initial attempt
    relayout();
    return () => { observers.forEach((o) => o.disconnect()); cancelAnimationFrame(raf); };
  }, []);

  return (
    <div className="lte-content-wrapper lte-layout-transparent-full">
      <PremiumAssetsLoader />
      <div className="lte-header-wrapper header-h1 header-parallax lte-header-overlay lte-layout-transparent-full lte-pageheader-disabled">
        <div id="lte-nav-wrapper" className="lte-layout-transparent-full lte-nav-color-white">
          <nav className="lte-navbar affix" data-spy="affix" data-offset-top="0">
            <div className="container">
              {/* Logo */}
              <div className="lte-navbar-logo">
                <a className="lte-logo" href="/">
                  {/* eslint-disable-next-line jsx-a11y/alt-text */}
                  <img src={assetUrl(clubLogo)} />
                </a>
              </div>
              {/* Navigation Items */}
              <div className="lte-navbar-items navbar-mobile-black navbar-collapse collapse" id="navbar" data-mobile-screen-width="1198">
                <div className="toggle-wrap">
                  <a className="lte-logo" href="/">
                    {/* eslint-disable-next-line jsx-a11y/alt-text */}
                    <img src={assetUrl(clubLogo)} />
                  </a>
                  <button type="button" className="lte-navbar-toggle collapsed" id="close-button">
                    <span className="close">&times;</span>
                  </button>
                  <div className="clearfix"></div>
                </div>
                {/* Navigation Menu */}
                <ul id="menu-main-menu" className="lte-ul-nav">
                  <li className="menu-item menu-item-type-custom current-menu-ancestor current-menu-parent">
                    <a href="/"><span>Domů</span></a>
                  </li>
                  <li className="menu-item menu-item-type-post_type menu-item-object-page">
                    <a href="/o-klubu"><span>O nás</span></a>
                  </li>
                  <li className="menu-item menu-item-type-custom">
                    <a href="/blog"><span>Blog</span></a>
                  </li>
                  <li className="menu-item menu-item-type-post_type menu-item-object-page">
                    <a href="/kontakt"><span>Kontakt</span></a>
                  </li>
                  <li className="menu-item menu-item-type-custom current-menu-ancestor current-menu-parent">
                    <a href="/#tym"><span>Tým</span></a>
                  </li>
                  <li className="menu-item menu-item-type-custom current-menu-ancestor current-menu-parent">
                    <a href="/#sponzori"><span>Sponzoři</span></a>
                  </li>
                  {!!galleryUrl && (
                    <li className="menu-item menu-item-type-custom current-menu-ancestor current-menu-parent">
                      <a target="_blank" href={galleryUrl}><span>{t('homepage.gallery')}</span></a>
                    </li>
                  )}
                  {/* Search toggle entry (theme script hooks on .lte-nav-search .lte-header) */}
                  <li className="menu-item lte-nav-search">
                    <a href="#" className="lte-header" onClick={(e) => e.preventDefault()}><span>Hledat</span></a>
                  </li>
                  {/* Auth links */}
                  {!isAuthenticated ? (
                    <>
                      <li className="menu-item menu-item-type-custom"><a href="/login"><span>Přihlásit</span></a></li>
                      <li className="menu-item menu-item-type-custom"><a href="/register"><span>Registrovat</span></a></li>
                    </>
                  ) : (
                    <>
                      <li className="menu-item menu-item-type-custom"><a href={accountHref}><span>Můj účet</span></a></li>
                      <li className="menu-item menu-item-type-custom">
                        <a href="#" onClick={(e) => { e.preventDefault(); try { logout(); } catch {} window.location.href = '/'; }}><span>Odhlásit</span></a>
                      </li>
                    </>
                  )}
                </ul>
                {/* Premium search wrapper (theme scripts control visibility) */}
                <div className="lte-top-search-wrapper" data-base-href="/hledat" data-source="site">
                  <a href="#" className="lte-top-search-ico" onClick={(e) => e.preventDefault()}></a>
                  <a href="#" className="lte-top-search-ico-close" onClick={(e) => e.preventDefault()}></a>
                  <div className="lte-top-search-field">
                    <input type="text" placeholder="Hledat…" />
                    <a href="#" id="lte-top-search-ico-mobile" onClick={(e) => e.preventDefault()}></a>
                  </div>
                </div>
              </div>
              {/* Mobile Menu Toggle */}
              <button type="button" className="lte-navbar-toggle" id="open-button">
                <span className="icon-bar top-bar"></span>
                <span className="icon-bar middle-bar"></span>
                <span className="icon-bar bottom-bar"></span>
              </button>
            </div>
          </nav>
        </div>
      </div>

      {/* Content */}
      {children}

      {/* Footer */}
      <div className="lte-footer-wrapper lte-footer-layout-default">
        <div className="footer-wrapper">
          <div className="lte-container">
            <div className="footer-block lte-footer-widget-area">
              <div className="elementor elementor-29393">
                <div className="elementor-element lte-background-black e-flex e-con-boxed e-con e-parent" data-settings='{"background_background":"classic"}'>
                  <div className="e-con-inner" style={{ paddingBottom: '92px' }}>
                    <div className="e-con-full e-flex e-con e-child">
                      <div className="elementor-widget elementor-widget-shortcode">
                        <div className="elementor-widget-container">
                          <div className="elementor-shortcode">
                            <a className="lte-logo" href="/">
                              {/* eslint-disable-next-line jsx-a11y/alt-text */}
                              <img src={assetUrl(clubLogo)} style={{ filter: 'drop-shadow(9px -1px 23px black)' }} />
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="elementor-widget elementor-widget-text-editor">
                        <div className="elementor-widget-container">
                          <p>
                            <span className="text-sm">
                              {clubName}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="elementor-widget elementor-widget-lte-elements">
                        <div className="elementor-widget-container">
                          <div className="lte-social lte-nav-second lte-type-">
                            <ul>
                              {!!s?.facebook_url && (
                                <li><a href={s.facebook_url} target="_blank" rel="noreferrer"><ion-icon name="logo-facebook" style={{ height: 22, width: 22 }}></ion-icon></a></li>
                              )}
                              {!!s?.instagram_url && (
                                <li><a href={s.instagram_url} target="_blank" rel="noreferrer"><ion-icon name="logo-instagram" style={{ height: 22, width: 22 }}></ion-icon></a></li>
                              )}
                              {!!s?.youtube_url && (
                                <li><a href={s.youtube_url} target="_blank" rel="noreferrer"><ion-icon name="logo-youtube" style={{ height: 22, width: 22 }}></ion-icon></a></li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <footer className="copyright-block copyright-layout-copyright-transparent">
          <div className="container">
            <p>
              <a href="https://tdvorak.dev" target="_blank" rel="noreferrer">TDvorak</a> &copy; {t('footer.all_rights_reserved', { year: new Date().getFullYear() })}
            </p>
          </div>
        </footer>
      </div>

      <a href="#" className="lte-go-top floating lte-go-top-icon">
        <span className="go-top-icon-v2 icon">
          <ion-icon name="football-outline" style={{ paddingRight: 2 }}></ion-icon>
        </span>
        <span className="go-top-header">Nahoru</span>
      </a>
    </div>
  );
};

const PremiumLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: settings } = usePublicSettings();
  const theme = useClubTheme();

  // Map club colors into Sparta CSS variables
  React.useEffect(() => {
    try {
      const root = document.documentElement as HTMLElement;
      root.style.setProperty('--colorAccent', theme.primaryColor);
      root.style.setProperty('--colorAccentHover', theme.secondaryColor || theme.primaryColor);
      root.style.setProperty('--colorBgPrimary', '#0e0e0e');
      root.style.setProperty('--colorBgSecondary', '#181818');
      root.style.setProperty('--colorBgTertiary', '#202020');
      root.style.setProperty('--colorTextPrimary', '#ffffff');
      root.style.setProperty('--colorTextSecondary', 'rgba(255,255,255,0.72)');
      root.style.setProperty('--colorBorder', 'rgba(255,255,255,0.16)');
    } catch {}
  }, [theme.primaryColor, theme.secondaryColor]);

  // Ensure Sparta style-pack is active while premium layout is mounted
  React.useEffect(() => {
    try {
      const all = ['style-pack-default', 'style-pack-modern', 'style-pack-minimal', 'style-pack-sparta'];
      all.forEach((c) => document.body.classList.remove(c));
      document.body.classList.add('style-pack-sparta');
    } catch {}
    return () => {
      try {
        document.body.classList.remove('style-pack-sparta');
      } catch {}
    };
  }, []);

  return (
    <Box minH="100vh" bg="var(--colorBgPrimary, #0e0e0e)" color="var(--colorTextPrimary, #ffffff)">
      <Box as="header">
        <SpartaNavbar />
      </Box>
      <Container maxW="container.xl" py={8}>
        {children}
      </Container>
      <Box as="section" mt={4}>
        <SponsorsSection layout="grid" theme="dark" />
      </Box>
      <Box as="footer" mt={0}>
        <Footer />
      </Box>
    </Box>
  );
};

export default PremiumLayout;
